import { Component } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { 
  SituViewWebApiClient, AccessoryBundle, ListAccessoryBundlesRequest, ListAccessoryBundlesResponse,
  CreateAccessoryBundleRequest, CreateAccessoryBundleResponse, FileParameter
} from 'situview-webapi-client';
import { RenameDialogComponent } from 'src/app/components/rename-dialog/rename-dialog.component';
import { AuthService, StateService, ConfirmDialogService } from 'src/app/services';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { ConfirmDialogData } from 'src/app/components/confirm-dialog/ConfirmDialogData';

@Component({
  selector: 'accessory-bundle-list',
  templateUrl: './accessory-bundle-list.component.html',
  styleUrls: ['./accessory-bundle-list.component.scss']
})
export class AccessoryBundleListComponent extends SituBaseComponent {
  loading: boolean = false;
  data: AccessoryBundle[] = [];
  searchTerm = "";

  constructor(
    authService: AuthService,
    stateService: StateService,
    private router: Router,
    private snackBar: MatSnackBar,
    private situViewApiClient: SituViewWebApiClient,
    private dialog: MatDialog,
    private dialogService: ConfirmDialogService
  ) { 
    super(authService, stateService);
  }

  protected onSelectedCompanyChange(): void {
    this.loadData();
  }

  add() {
    this.router.navigate(['admin/accessory-bundle/add']);
  }

  onClickAccessoryBundle(item: AccessoryBundle) {
    this.router.navigate(['admin/accessory-bundle', item.id ]);
  }

  loadData() {
    this.loading = true;

    let request = new ListAccessoryBundlesRequest();
    request.companyId = this.selectedCompany?.id;
    if (this.searchTerm) request.searchTerm = this.searchTerm;

    this.situViewApiClient.listAccessoryBundles(request)
      .then((response: ListAccessoryBundlesResponse) => {
        if (response.success) {
          this.data = response.accessoryBundles ?? [];
        } else {
          this.showMessage("There was an issue retrieving accessory bundles!");
        }
        this.loading = false;
      })
      .catch((err: any) => {
        this.loading = false;
      });
  }

  uploadCSV() {
    if (!this.selectedCompany?.code) {
      this.showMessage("Please select a company first.");
      return;
    }

    // Show confirmation dialog before proceeding
    this.dialogService.open({
      title: 'Import Accessory Bundles',
      message: `Are you sure you want to import accessory bundles for ${this.selectedCompany.code}? This will create new accessory bundles based on the CSV data.`,
      cancelText: 'Cancel',
      confirmText: 'Import Accessory Bundles',
      actionIcon: 'file_upload',
      actionColour: 'primary'
    } as ConfirmDialogData);

    this._subscriptions.push(this.dialogService.confirmed()
      .subscribe(confirmed => {
        if (confirmed) {
          this.proceedWithCSVUpload();
        }
      }));
  }

  private proceedWithCSVUpload() {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.csv';
    fileInput.style.display = 'none';
    
    fileInput.onchange = (event: any) => {
      const file = event.target.files[0];
      if (!file) return;
      
      const fileExt = file.name.split('.').pop()?.toLowerCase();
      if (fileExt !== 'csv') {
        this.showMessage("Only CSV files are allowed");
        return;
      }
      
      this.loading = true;
      
      // Create FileParameter object for the API
      const fileParameter = {
        data: file,
        fileName: file.name
      };
      
      // Note: CSV import functionality not available for accessory bundles
      this.loading = false;
      this.showMessage('CSV import is not currently available for accessory bundles');
    };
    
    document.body.appendChild(fileInput);
    fileInput.click();
    document.body.removeChild(fileInput);
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }
}